import { EnvironmentDetail, LatencyMetric } from '@amzn/ihm-associate-tools-js-lib'
import { SiteAssistModalUtils } from '@utils/SiteAssistModalUtils'
import { IIBTokenProvider } from './utils/IIBTokenProvider'

interface ISiteAssistantContext {
    readonly toolName: string
    readonly siteId: string
}

// https://tiny.amazon.com/3rrb1os0/GrocerySiteAssistantCoreServiceLambda/mainline/APIGatewayWebSocketEventBody.java
interface IInvokeModelRequest {
    readonly action: string
    readonly siteAssistantAgentContext: ISiteAssistantContext
    readonly conversationTime: string
    readonly userQuery: string
    readonly token: string
}

export type DisconnectFunction = () => void

export class WebSocketService {
    private static instance: WebSocketService
    private wsEndpoint: string = ''
    private ws: WebSocket | null = null
    private reconnectAttempts = 0
    private maxReconnectAttempts = 5
    private reconnectDelay = 1000 // Start with 1 second
    private isConnecting = false
    private connectedEvent = 'WSConnected'
    private connectErrorEvent = 'WSConnectionError'
    private wsCloseEvent = 'WSClose'
    private wsErrorEvent = 'WSError'
    private wsResponseEvent = 'WSResponse'

    private STREAMING_ENDPOINTS = {
        test: 'wss://xiow8tmthg.execute-api.us-west-2.amazonaws.com/beta/',
        gamma: 'wss://2xqyj4vdeh.execute-api.us-east-1.amazonaws.com/gamma/',
        prod: 'wss://lb2usxnie2.execute-api.us-east-1.amazonaws.com/prod/',
    }

    public static getInstance (): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService()
        }
        return WebSocketService.instance
    }

    /**
     * Initialize WebSocket connection
     */
    public async connect (): Promise<void> {
        console.log('WebsocketService: Attempting to connect to site assistant')
        if (this.ws?.readyState === WebSocket.OPEN) {
            SiteAssistModalUtils.sendSAEvent(this.connectedEvent, '')
            console.log(
                `WebsocketService: Aborting connection attempt. ReadyState: ${this.ws?.readyState}, IsConnecting: ${this.isConnecting}`,
            )
            return
        }

        if (this.isConnecting) {
            console.log(
                `WebsocketService: Aborting connection attempt. ReadyState: ${this.ws?.readyState}, IsConnecting: ${this.isConnecting}`,
            )
            return
        }

        this.isConnecting = true
        const iibToken = await IIBTokenProvider.getIdToken()

        try {
            this.ws = new WebSocket(`${this.wsEndpoint}?token=${iibToken}`)
            console.log('WebsocketService: Connected to site assistant')
            this.ws.onopen = () => {
                this.isConnecting = false
                this.reconnectAttempts = 0
                this.reconnectDelay = 1000
                SiteAssistModalUtils.sendSAEvent(this.connectedEvent, '')
            }

            this.ws.onmessage = async (event) => {
                if (!event.data) {
                    return
                }
                await SiteAssistModalUtils.sendSAEvent(this.wsResponseEvent, event.data)
            }

            this.ws.onclose = async (event) => {
                console.log('WebsocketService: Connection closed')
                this.isConnecting = false
                await SiteAssistModalUtils.sendSAEvent(this.wsCloseEvent, '')
                // Attempt to reconnect
                if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.scheduleReconnect()
                }
            }

            this.ws.onerror = async (error) => {
                console.error('WebSocket error:', error)
                this.isConnecting = false
                await SiteAssistModalUtils.sendSAEvent(this.wsErrorEvent, '')
            }
        } catch (error) {
            this.isConnecting = false
            await SiteAssistModalUtils.sendSAEvent(this.connectErrorEvent, error.message)
            throw new Error(`Failed to create WebSocket connection: ${error}`)
        }
    }

    /**
     * Send a message through the WebSocket
     */
    public async sendMessage (storeId: string, currentTool: string, messageId: string, userQuery: string): Promise<void> {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket is not connected')
        }
        if (!storeId || !currentTool || !userQuery) {
            throw new Error('Invalid message parameters')
        }

        const iibToken = await IIBTokenProvider.getIdToken()

        const message: IInvokeModelRequest = {
            action: 'invokeModel',
            conversationTime: messageId,
            siteAssistantAgentContext: {
                toolName: currentTool,
                siteId: storeId,
            },
            userQuery,
            token: iibToken,
        }

        this.ws.send(JSON.stringify(message))
    }

    /**
     * Check if WebSocket is connected
     */
    public isConnected (): boolean {
        return this.ws?.readyState === WebSocket.OPEN
    }

    /**
     * Disconnect WebSocket
     */
    public disconnect (): void {
        if (this.ws) {
            this.ws.close(1000, 'Normal closure')
            this.ws = null
        }
    }

    private constructor () {
        const domain = EnvironmentDetail.getDomain()
        this.wsEndpoint = this.STREAMING_ENDPOINTS[domain]
    }

    /**
     * Schedule reconnection attempt
     */
    private scheduleReconnect (): void {
        this.reconnectAttempts++
        console.log(
            `Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${this.reconnectDelay}ms`,
        )

        setTimeout(() => {
            this.connect().catch((error) => {
                console.error('Reconnection failed:', error)
                this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000) // Max 30 seconds
            })
        }, this.reconnectDelay)
    }
}
